import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { TestUserManager } from "@/components/debug/TestUserManager";
import { supabase } from "@/lib/supabase";
import { Loader2, Wrench, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useAuth();
  const isDebugMode = import.meta.env.DEV || (typeof window !== "undefined" && (window as any).__DEBUG_MODE);

  useEffect(() => {
    if (!authLoading && user) {
      setLocation("/dashboard");
    }
  }, [authLoading, user, setLocation]);

  const performLogin = async (loginEmail: string, loginPassword: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });
      if (error) throw error;
      setLocation("/dashboard");
    } catch (err: any) {
      let message: string;
      if (err.message === "Invalid login credentials") {
        message = "E-mail ou senha incorretos";
      } else if (err.message === "Email not confirmed") {
        message = "E-mail não confirmado. Verifique sua caixa de entrada.";
      } else if (err.message === "User not found") {
        message = "Usuário não encontrado";
      } else if (err.message === "Too many requests") {
        message = "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
      } else {
        message = err.message || "Erro ao fazer login";
      }
      setError(message);
      toast({
        title: "Erro de Autenticação",
        description: message,
        variant: "destructive",
      });
      throw err;
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await performLogin(email, password);
    } finally {
      setLoading(false);
    }
  };

  const handleTestUserLogin = async (testEmail: string, testPassword: string) => {
    setLoading(true);
    setError("");
    try {
      await performLogin(testEmail, testPassword);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      {isDebugMode && (
        <div className="fixed top-4 right-4 z-50">
          <TestUserManager
            onSelectUser={handleTestUserLogin}
            isLoading={loading}
          />
        </div>
      )}
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center shadow-xl shadow-primary/30 rotate-3">
            <Wrench className="w-8 h-8 text-white" />
          </div>
        </div>

        <Card className="p-8 border-none shadow-2xl">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold font-display text-slate-900">
              Bem-vindo de volta
            </h1>
            <p className="text-muted-foreground mt-2">
              Insira suas credenciais para acessar o painel
            </p>
          </div>

          <form
            onSubmit={handleLogin}
            className="space-y-4"
            data-testid="form-login"
          >
            <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="nome@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-50 border-slate-200"
                  data-testid="input-email"
                />
              </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-slate-50 border-slate-200 pr-10"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  data-testid="button-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                data-testid="text-login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 text-base shadow-lg shadow-primary/25 mt-4"
              disabled={loading}
              data-testid="button-login"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Entrar
            </Button>
          </form>

          <div className="text-center mt-6">
            <p className="text-sm text-muted-foreground">
              Ainda não tem conta?{" "}
              <a
                href="/registrar"
                className="text-primary hover:underline font-medium"
                data-testid="link-registrar"
              >
                Cadastre-se aqui
              </a>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
